import { Link, useLocation } from "react-router-dom";
import type { ThisWeekItem } from "../types/dashboard";
import { zonedParts } from "../lib/timezone";

interface Props {
  items: ThisWeekItem[];
  timeZone: string;
}

export default function ThisWeek({ items, timeZone }: Props) {
  const location = useLocation();

  return (
    <div className="dashboard__section">
      <h2 id="this-week-title" className="dashboard__section-label">
        This week
      </h2>
      <section className="panel" aria-labelledby="this-week-title">
        {items.length === 0 ? (
          <p className="panel__empty">No upcoming Masses in the next seven days.</p>
        ) : (
          <ul className="this-week__list">
            {items.map((m) => (
              <ThisWeekRow
                key={m.massId}
                item={m}
                timeZone={timeZone}
                backgroundLocation={location}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

interface RowProps {
  item: ThisWeekItem;
  timeZone: string;
  backgroundLocation: ReturnType<typeof useLocation>;
}

// Weekend = Saturday Vigil (5pm+) and any Sunday Mass — the ones
// Colleen's eye should land on first, so they "reach up" (larger +
// semibold). Everything else (Mon-Fri at any hour, including OLG's
// Wed 6pm weekday evening Mass, plus Saturday before the vigil) keeps
// the plain daily treatment — not diminished, just not elevated.
// startsAt is a real UTC instant; read day/hour in the parish
// timezone.
function isWeekendMass(startsAt: string, timeZone: string): boolean {
  const { weekday, hour } = zonedParts(new Date(startsAt), timeZone);
  return weekday === 0 || (weekday === 6 && hour >= 17);
}

// Every row opens the Mass modal (/mass/:id) — one consistent modal per
// Mass showing its coverage gaps (sub-requests + unfilled slots) and
// roster. The "N gaps" count matches the modal's Needs-coverage rows.
function ThisWeekRow({ item, timeZone, backgroundLocation }: RowProps) {
  const weekendClass = isWeekendMass(item.startsAt, timeZone) ? " this-week__item--weekend" : "";
  const statusText =
    item.status === "all_filled"
      ? "all set ✓"
      : `${item.gapCount} ${item.gapCount === 1 ? "gap" : "gaps"}`;

  return (
    <li className={`this-week__item${weekendClass}`}>
      <Link
        className="this-week__row-link"
        to={`/mass/${item.massId}`}
        state={{ backgroundLocation }}
      >
        <span className="this-week__label">{item.label}</span>
        <span className={`this-week__status this-week__status--${item.status}`}>
          {statusText}
        </span>
      </Link>
    </li>
  );
}
